import { Container, Sprite, Texture, Ticker, Rectangle } from 'pixi.js'
import { tween, pulseAlpha, Easing } from './animations'

/** Width of the idle avatar in CSS pixels */
export const AVATAR_WIDTH = 200
/** Height matches the Canva video aspect ratio (854x480 ≈ 16:9). */
export const AVATAR_HEIGHT = AVATAR_WIDTH * (480 / 854)
/** Width of the live avatar during conversation (larger) */
const LIVE_AVATAR_WIDTH = 280

export class AvatarRenderer {
  /** PixiJS container for the speech bubble hit-area and live avatar sprite. */
  readonly container: Container
  private idleVideoEl: HTMLVideoElement | null = null
  private idleGroup: Container | null = null
  private liveSprite: Sprite | null = null
  private cancelGlow: (() => void) | null = null
  private cancelEntrance: (() => void) | null = null
  private visible = false
  private stageWidth: number
  private stageHeight: number
  /** The widget root DOM element — idle video is appended here as a sibling of the canvas. */
  private widgetRoot: HTMLElement | null = null

  constructor(stageWidth: number, stageHeight: number) {
    this.stageWidth = stageWidth
    this.stageHeight = stageHeight
    this.container = new Container()
    this.container.visible = false
    this.container.x = stageWidth / 2 // center horizontally
    this.container.y = stageHeight + AVATAR_HEIGHT // start off-screen below
  }

  /**
   * Load the idle loop video as a DOM <video> element (not a PixiJS sprite).
   * VP9 alpha WebM transparency is handled natively by the browser compositor.
   * The video is placed inside the widget root div, behind the PixiJS canvas.
   */
  async loadIdleVideo(videoUrl: string, widgetRoot: HTMLElement): Promise<void> {
    this.widgetRoot = widgetRoot

    this.idleVideoEl = document.createElement('video')
    this.idleVideoEl.src = videoUrl
    this.idleVideoEl.loop = true
    this.idleVideoEl.muted = true
    this.idleVideoEl.playsInline = true
    this.idleVideoEl.crossOrigin = 'anonymous'
    Object.assign(this.idleVideoEl.style, {
      position: 'absolute',
      bottom: '10px',
      left: '50%',
      transform: 'translateX(-50%) translateY(100%)', // start off-screen
      width: `${AVATAR_WIDTH}px`,
      height: 'auto',
      pointerEvents: 'none',
      zIndex: '0', // behind the PixiJS canvas
      transition: 'none',
    })

    // Insert before the canvas so it renders behind it
    widgetRoot.insertBefore(this.idleVideoEl, widgetRoot.firstChild)

    await this.idleVideoEl.play()

    // Create a minimal PixiJS container for the hit area + speech bubble positioning
    this.idleGroup = new Container()
    this.container.addChild(this.idleGroup)

    // Position PixiJS container to match the video position
    this.container.x = this.stageWidth / 2
    this.container.y = this.stageHeight + AVATAR_HEIGHT // still off-screen
  }

  /**
   * Load from a static image instead of video (fallback).
   */
  async loadIdleImage(imageUrl: string): Promise<void> {
    const texture = Texture.from(imageUrl)
    const sprite = new Sprite(texture)
    sprite.width = AVATAR_WIDTH
    sprite.height = AVATAR_HEIGHT
    sprite.anchor.set(0.5, 1)

    this.idleGroup = new Container()
    this.idleGroup.addChild(sprite)
    this.container.addChild(this.idleGroup)

    this.container.x = this.stageWidth / 2
    this.container.y = this.stageHeight + AVATAR_HEIGHT
  }

  /** Slide the avatar up into view. */
  slideIn(ticker: Ticker): void {
    if (this.visible) return
    this.visible = true

    const targetY = this.stageHeight - 10 // 10px padding from bottom

    // PixiJS container (for speech bubble positioning / hit area)
    this.container.alpha = 1
    this.container.visible = true
    this.container.y = this.stageHeight + AVATAR_HEIGHT

    this.cancelEntrance = tween({
      target: this.container,
      props: { y: targetY },
      duration: 700,
      easing: Easing.easeOutBack,
      ticker,
    })

    // Slide the DOM video element in sync
    if (this.idleVideoEl) {
      // Use CSS transition for the slide animation
      this.idleVideoEl.style.transition = 'transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)'
      this.idleVideoEl.style.transform = 'translateX(-50%) translateY(0)'
    }
  }

  /** Slide the avatar down out of view. */
  slideOut(ticker: Ticker): void {
    if (!this.visible) return
    this.visible = false

    this.cancelGlow?.()
    this.cancelGlow = null

    // PixiJS container
    tween({
      target: this.container,
      props: { y: this.stageHeight + AVATAR_HEIGHT },
      duration: 450,
      easing: Easing.easeInCubic,
      ticker,
      onComplete: () => {
        this.container.visible = false
      },
    })

    // Slide DOM video down
    if (this.idleVideoEl) {
      this.idleVideoEl.style.transition = 'transform 0.45s cubic-bezier(0.32, 0, 0.67, 0)'
      this.idleVideoEl.style.transform = 'translateX(-50%) translateY(100%)'
    }
  }

  get isVisible(): boolean {
    return this.visible
  }

  /** Make the avatar container interactive (clickable). */
  setInteractive(onClick: () => void): void {
    this.container.eventMode = 'static'
    this.container.cursor = 'pointer'

    // Invisible hit area — no rendered graphics, just pointer detection
    this.container.hitArea = new Rectangle(
      -AVATAR_WIDTH / 2,
      -AVATAR_HEIGHT,
      AVATAR_WIDTH,
      AVATAR_HEIGHT,
    )

    this.container.on('pointertap', onClick)

    // Also make the DOM video clickable
    if (this.idleVideoEl) {
      this.idleVideoEl.style.pointerEvents = 'auto'
      this.idleVideoEl.style.cursor = 'pointer'
      this.idleVideoEl.addEventListener('click', onClick)
    }
  }

  /**
   * Switch from idle video to live avatar video.
   * Hides the DOM idle video and shows a PixiJS sprite for the live stream.
   */
  setLiveVideo(videoEl: HTMLVideoElement, ticker: Ticker): void {
    const createSprite = () => {
      const texture = Texture.from(videoEl)
      this.liveSprite = new Sprite(texture)

      const vw = videoEl.videoWidth || 1
      const vh = videoEl.videoHeight || 1
      const scale = LIVE_AVATAR_WIDTH / vw
      this.liveSprite.width = LIVE_AVATAR_WIDTH
      this.liveSprite.height = vh * scale

      this.liveSprite.anchor.set(0.5, 1)
      this.liveSprite.alpha = 0

      this.container.addChild(this.liveSprite)

      // Hide idle DOM video
      if (this.idleVideoEl) this.idleVideoEl.style.display = 'none'
      if (this.idleGroup) this.idleGroup.visible = false

      this.cancelGlow?.()
      this.cancelGlow = null

      tween({
        target: this.liveSprite,
        props: { alpha: 1 },
        duration: 500,
        easing: Easing.easeInOutSine,
        ticker,
      })
    }

    if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
      createSprite()
    } else {
      const onReady = () => {
        videoEl.removeEventListener('loadeddata', onReady)
        videoEl.removeEventListener('resize', onReady)
        createSprite()
      }
      videoEl.addEventListener('loadeddata', onReady)
      videoEl.addEventListener('resize', onReady)
    }
  }

  /** Switch back from live video to idle video. */
  clearLiveVideo(ticker: Ticker): void {
    if (this.liveSprite) {
      tween({
        target: this.liveSprite,
        props: { alpha: 0 },
        duration: 400,
        easing: Easing.easeInCubic,
        ticker,
        onComplete: () => {
          this.liveSprite?.destroy()
          this.liveSprite = null
        },
      })
    }

    // Show idle DOM video again
    if (this.idleVideoEl) this.idleVideoEl.style.display = ''
    if (this.idleGroup) this.idleGroup.visible = true
  }

  destroy(): void {
    this.cancelGlow?.()
    this.cancelEntrance?.()
    if (this.idleVideoEl) {
      this.idleVideoEl.pause()
      this.idleVideoEl.remove()
    }
    this.container.destroy({ children: true })
  }
}
